import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Maximize2, ChevronDown, Plus, Clock } from 'lucide-react';
import { useTeam } from 'shared/context/teamContext';
import ChatPage from 'features/dashboard/pages/ChatPage';
import ChatHistoryPopover from 'features/dashboard/components/ChatHistoryPopover';

/**
 * Ask, beside whatever you were looking at.
 *
 * The panel is a shell: the conversation inside it is the same component that
 * renders the full page, in `variant="panel"`. Everything that makes Ask
 * useful — clarify questions, plan-gate approvals, answer feedback, tables
 * that open the record they came from — behaves identically, because it is the
 * same code and not a second implementation of it.
 *
 * The panel is a flex sibling of the page content rather than an overlay, so
 * the page shrinks instead of being covered. Below 1100px there is not enough
 * room for both, and it overlays with a scrim.
 */

const MIN_W = 360;
const MAX_W = 760;
const DEFAULT_W = 440;
const WIDTH_KEY = 'trodo.askPanel.width';
const OVERLAY_BELOW = 1100;

function readWidth() {
  try {
    const raw = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isFinite(raw) && raw >= MIN_W && raw <= MAX_W) return raw;
  } catch (_) { /* private mode */ }
  return DEFAULT_W;
}

export default function AskPanel({ open, onClose, draft = null, resume = null }) {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const teamSlug = selectedTeam?.slug || selectedTeam?.name || (teamId != null ? String(teamId) : null);
  const location = useLocation();
  const navigate = useNavigate();

  const [width, setWidth] = useState(readWidth);
  const widthRef = useRef(width);
  widthRef.current = width;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [overlay, setOverlay] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < OVERLAY_BELOW : false));
  // Bumping this remounts the conversation, which is what "New chat" means
  // here — the panel holds its session in state, so a fresh mount is a fresh
  // thread without touching the URL of whatever page is underneath.
  const [chatEpoch, setChatEpoch] = useState(0);
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  // A drafted message starts a fresh conversation and belongs to that one
  // conversation only: "New chat" afterwards starts empty.
  const [draftFor, setDraftFor] = useState(null); // { draft, epoch }
  // `resume` ({sessionId, nonce}) reopens an existing conversation here — how
  // a conversation that built a dashboard follows it from the full chat page
  // to the dashboard's side panel (TRO-469).
  useEffect(() => {
    if (!resume || !resume.nonce || !resume.sessionId) return;
    setResumeSessionId(String(resume.sessionId));
    setChatEpoch((n) => n + 1);
  }, [resume]);

  useEffect(() => {
    if (!draft || !draft.nonce) return;
    setResumeSessionId(null);
    setChatEpoch((n) => {
      setDraftFor({ draft, epoch: n + 1 });
      return n + 1;
    });
  }, [draft]);

  useEffect(() => {
    const onResize = () => setOverlay(window.innerWidth < OVERLAY_BELOW);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev) => setWidth(Math.min(MAX_W, Math.max(MIN_W, startW + (startX - ev.clientX))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { localStorage.setItem(WIDTH_KEY, String(widthRef.current)); } catch (_) { /* private mode */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Published so drawer overlays stop at the panel's edge instead of
  // covering it.
  useEffect(() => {
    if (!open || overlay) { document.documentElement.style.removeProperty('--trodo-ask-w'); return undefined; }
    document.documentElement.style.setProperty('--trodo-ask-w', `${width}px`);
    return () => document.documentElement.style.removeProperty('--trodo-ask-w');
  }, [open, overlay, width]);

  const startNewChat = useCallback(() => {
    setResumeSessionId(null);
    setChatEpoch((n) => n + 1);
  }, []);

  const screenContext = useMemo(
    () => ({ pathname: location.pathname, search: location.search }),
    [location.pathname, location.search],
  );

  // The same conversation, full page. A question still running keeps running:
  // Ask runs are durable on the server, and the page picks the session up
  // where the panel left it.
  const openFull = useCallback(() => {
    onClose?.();
    const base = `/${encodeURIComponent(teamSlug || '')}/chat`;
    navigate(sessionId ? `${base}?session=${encodeURIComponent(sessionId)}` : base);
  }, [navigate, teamSlug, onClose, sessionId]);

  if (!open) return null;

  const panel = (
    <aside
      className={`relative flex min-h-0 w-full flex-1 flex-col overflow-hidden border-l border-[color:var(--border-default)] bg-[color:var(--surface-base)] ${
        overlay ? '' : 'rounded-tr-2xl border-t'
      }`}
      aria-label="Lucid"
    >
      {!overlay && (
        <div
          onMouseDown={startResize}
          title="Drag to resize"
          className="absolute left-0 top-0 bottom-0 z-10 -ml-0.5 w-1 cursor-col-resize hover:bg-[color:var(--trodo-primary)]/30"
        />
      )}

      {/* Header: new chat + history, expand, close: the three things you do
          to the panel itself, kept off the conversation below. */}
      <div className="relative flex shrink-0 items-center gap-1 border-b border-[color:var(--border-default)] px-2 py-2">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={historyOpen}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover"
        >
          New chat
          <ChevronDown size={13} strokeWidth={2} className="text-[color:var(--text-muted)]" />
        </button>
        {historyOpen && teamId && (
          <ChatHistoryPopover
            className="left-2 top-full mt-1"
            teamId={teamId}
            activeSessionId={sessionId}
            onSelect={(sid) => { setResumeSessionId(sid); setChatEpoch((n) => n + 1); }}
            onNewChat={startNewChat}
            onDeleted={(sid) => { if (sessionId != null && String(sid) === String(sessionId)) startNewChat(); }}
            onClose={() => setHistoryOpen(false)}
          />
        )}
        <button
          type="button"
          onClick={startNewChat}
          title="Start a new conversation"
          className="rounded-md p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
        >
          <Plus size={14} strokeWidth={2} />
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={openFull}
          title="Open in full screen"
          className="rounded-md p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
        >
          <Maximize2 size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={onClose}
          title="Close Lucid"
          className="rounded-md p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
        >
          <X size={15} strokeWidth={2} />
        </button>
      </div>

      {/* The conversation: the same component the full page renders. */}
      <div className="flex min-h-0 flex-1 flex-col">
        <ChatPage
          key={`ask-panel-${chatEpoch}`}
          draft={draftFor && draftFor.epoch === chatEpoch ? draftFor.draft : null}
          variant="panel"
          screenContext={screenContext}
          resumeSessionId={resumeSessionId}
          onOpenFull={openFull}
          onSessionChange={setSessionId}
        />
      </div>
    </aside>
  );

  // The panel is one of the app's panels, not a layer on top of it: the same
  // grey strip runs above it as above the page, so both start on the same line
  // and end on the same line. Beside the page it is a column in the app row;
  // overlaid (narrow viewports) it is a drawer and takes the full height.
  const body = (
    <div
      className={`flex shrink-0 flex-col overflow-hidden bg-[color:var(--surface-sunken)] ${overlay ? '' : 'pt-2'}`}
      style={{ width: overlay ? 'min(100vw, 420px)' : width }}
    >
      {panel}
    </div>
  );

  if (overlay) {
    return (
      <>
        <div className="fixed inset-0 z-[60] bg-black/15" onClick={onClose} />
        <div className="fixed right-0 top-0 bottom-0 z-[61] flex">{body}</div>
      </>
    );
  }
  return body;
}

export { Clock };
